import * as React from "react";
import { useState } from "react";
import copy from "copy-to-clipboard";
import { Copy as CopyIcon, Check, FileText } from "lucide-react";

import { VideoScript } from "@/interfaces";
import { Dialog, Button } from "@/components/ui";

interface Props {
  open: boolean;
  videoScript: VideoScript;
  onClose?: () => void;
}

const VideoScriptModalComponent: React.FC<Props> = ({ open, videoScript, onClose }) => {
  const [copiedVideoScript, setCopiedVideoScript] = useState<boolean>(false);

  const copyVideoScript = (content: string) => {
    copy(content);
    if (copiedVideoScript) return;

    setCopiedVideoScript(true);
    setTimeout(() => {
      setCopiedVideoScript(false);
    }, 2000);
  };

  const selectedMediaContent = () => {
    if (videoScript.content) {
      return videoScript.content.split("\n").map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ));
    }
    return (
      <div className="text-center italic text-slate-400 py-6 text-xs font-medium">
        No content available for this script.
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose?.();
      }}
      size="lg"
    >
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold">
          <FileText className="size-5 text-indigo-600" />
          <span>{videoScript.title || "Video Script"}</span>
        </Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="p-5 max-h-[60vh] overflow-y-auto flex flex-col gap-4">
        <div className="text-slate-600 text-sm whitespace-pre-wrap leading-relaxed select-text font-normal">
          {selectedMediaContent()}
        </div>
      </Dialog.Content>

      <Dialog.Footer className="flex flex-row justify-end gap-2.5 p-5 border-t border-slate-100 select-none">
        <Button
          data-elm-id="videoScriptModalCopyBtn"
          variant="outline"
          size="sm"
          className="flex items-center gap-1.5 font-semibold text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50/50 hover:text-indigo-700"
          onClick={() => copyVideoScript(videoScript.content)}
        >
          {copiedVideoScript ? (
            <>
              <Check className="size-3.5" />
              Copied!
            </>
          ) : (
            <>
              <CopyIcon className="size-3.5" />
              Copy Script
            </>
          )}
        </Button>
        <Button
          data-elm-id="videoScriptModalCloseBtn"
          variant="default"
          size="sm"
          className="font-semibold text-xs px-4"
          onClick={onClose}
        >
          Close
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};

export const VideoScriptModal = VideoScriptModalComponent;
